import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API_BASE } from "@/App";
import { BookOpen, Clock, CheckCircle, XCircle, AlertTriangle, ArrowRight, Timer } from "lucide-react";

/**
 * GS Mini Diagnostic Test
 * Short 15-20 minute test to assess GS foundation
 * Based on CLARIAS SUPREME SPEC Step 5
 */

const GSDiagnosticPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes
  const [testStarted, setTestStarted] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Auth guard - redirect if logged out during test
  useEffect(() => {
    if (!user || !token) {
      navigate('/auth', { replace: true });
    }
  }, [user, token, navigate]);

  useEffect(() => {
    if (user && token) {
      fetchQuestions();
    }
  }, [user, token]);

  useEffect(() => {
    if (!testStarted || testComplete) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testStarted, testComplete]);

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/command/diagnostic/gs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
      setQuestions(generateFallbackQuestions());
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackQuestions = () => {
    return [
      {
        id: 1,
        question: "Which Article of the Indian Constitution deals with the Right to Equality?",
        options: { A: "Article 14", B: "Article 19", C: "Article 21", D: "Article 32" },
        correct: "A",
        subject: "Polity",
        difficulty: "easy"
      },
      {
        id: 2,
        question: "The Indus Valley Civilization was mainly spread across which river valley?",
        options: { A: "Ganga", B: "Yamuna", C: "Indus", D: "Brahmaputra" },
        correct: "C",
        subject: "History",
        difficulty: "easy"
      },
      {
        id: 3,
        question: "Which Schedule of the Constitution contains provisions regarding Panchayati Raj?",
        options: { A: "9th Schedule", B: "10th Schedule", C: "11th Schedule", D: "12th Schedule" },
        correct: "C",
        subject: "Polity",
        difficulty: "medium"
      },
      {
        id: 4,
        question: "The Battle of Plassey was fought in which year?",
        options: { A: "1757", B: "1764", C: "1857", D: "1947" },
        correct: "A",
        subject: "History",
        difficulty: "easy"
      },
      {
        id: 5,
        question: "Which of the following is NOT a Fundamental Right under the Indian Constitution?",
        options: { A: "Right to Equality", B: "Right to Property", C: "Right to Freedom", D: "Right against Exploitation" },
        correct: "B",
        subject: "Polity",
        difficulty: "easy"
      },
      {
        id: 6,
        question: "The monsoon season in India generally occurs during:",
        options: { A: "March-May", B: "June-September", C: "October-November", D: "December-February" },
        correct: "B",
        subject: "Geography",
        difficulty: "easy"
      },
      {
        id: 7,
        question: "Which Constitutional Amendment introduced the Anti-Defection Law?",
        options: { A: "42nd", B: "44th", C: "52nd", D: "73rd" },
        correct: "C",
        subject: "Polity",
        difficulty: "medium"
      },
      {
        id: 8,
        question: "The Quit India Movement was launched in:",
        options: { A: "1940", B: "1942", C: "1944", D: "1946" },
        correct: "B",
        subject: "History",
        difficulty: "easy"
      },
      {
        id: 9,
        question: "Which soil type is most suitable for cotton cultivation?",
        options: { A: "Alluvial", B: "Black", C: "Red", D: "Laterite" },
        correct: "B",
        subject: "Geography",
        difficulty: "easy"
      },
      {
        id: 10,
        question: "The Finance Commission is constituted under which Article?",
        options: { A: "Article 280", B: "Article 300", C: "Article 320", D: "Article 340" },
        correct: "A",
        subject: "Polity",
        difficulty: "medium"
      },
      {
        id: 11,
        question: "Who was the first Governor-General of independent India?",
        options: { A: "Lord Mountbatten", B: "C. Rajagopalachari", C: "Dr. Rajendra Prasad", D: "Jawaharlal Nehru" },
        correct: "A",
        subject: "History",
        difficulty: "easy"
      },
      {
        id: 12,
        question: "The Western Ghats are also known as:",
        options: { A: "Sahyadris", B: "Nilgiris", C: "Vindhyas", D: "Aravallis" },
        correct: "A",
        subject: "Geography",
        difficulty: "easy"
      },
      {
        id: 13,
        question: "Which body conducts elections in India?",
        options: { A: "Parliament", B: "Election Commission", C: "Supreme Court", D: "UPSC" },
        correct: "B",
        subject: "Polity",
        difficulty: "easy"
      },
      {
        id: 14,
        question: "The Dandi March was undertaken for which cause?",
        options: { A: "Against Jallianwala Bagh", B: "Against Salt Tax", C: "For Independence", D: "Against Rowlatt Act" },
        correct: "B",
        subject: "History",
        difficulty: "easy"
      },
      {
        id: 15,
        question: "Which planet is known as the 'Red Planet'?",
        options: { A: "Jupiter", B: "Mars", C: "Saturn", D: "Venus" },
        correct: "B",
        subject: "Science",
        difficulty: "easy"
      }
    ];
  };

  const handleAnswer = (optionKey) => {
    setAnswers({ ...answers, [questions[currentIndex].id]: optionKey });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    let correct = 0;
    let wrong = 0;
    let skipped = 0;
    const subjectScores = {};
    
    questions.forEach((q) => {
      const userAnswer = answers[q.id];
      if (!subjectScores[q.subject]) {
        subjectScores[q.subject] = { correct: 0, total: 0 };
      }
      subjectScores[q.subject].total++;
      
      if (!userAnswer) {
        skipped++;
      } else if (userAnswer === q.correct) {
        correct++;
        subjectScores[q.subject].correct++;
      } else {
        wrong++;
      }
    });
    
    const totalAttempted = correct + wrong;
    const accuracy = totalAttempted > 0 ? (correct / totalAttempted) * 100 : 0;
    const gsScore = Math.round(accuracy);
    
    // Find weakest subject
    let weakestSubject = null;
    let weakestAccuracy = 100;
    Object.entries(subjectScores).forEach(([subject, scores]) => {
      const acc = scores.total > 0 ? (scores.correct / scores.total) * 100 : 0;
      if (acc < weakestAccuracy) {
        weakestAccuracy = acc;
        weakestSubject = subject;
      }
    });
    
    let status, reason, instruction;
    if (gsScore >= 60) {
      status = "safe";
      reason = "Your GS foundation is solid. Continue building on this.";
      instruction = "Focus on revision and reducing careless errors.";
    } else if (gsScore >= 40) {
      status = "borderline";
      reason = `Your accuracy is borderline. ${weakestSubject} needs attention.`;
      instruction = `Strengthen ${weakestSubject} basics before attempting more tests.`;
    } else {
      status = "unsafe";
      reason = "Your GS accuracy is below safe threshold.";
      instruction = "Go back to static syllabus. Build fundamentals first.";
    }
    
    const resultData = {
      score: gsScore,
      correct,
      wrong,
      skipped,
      total: questions.length,
      status,
      reason,
      instruction,
      weakest_subject: weakestSubject,
      subject_scores: subjectScores
    };
    
    setResult(resultData);
    setTestComplete(true);
    
    // Submit to backend - complete action AND record behavior for analytics
    try {
      const token = localStorage.getItem("token");
      
      // 1. Complete the forced action
      await managedAxios.post(
        `${API_BASE}/command/complete-action`,
        null,
        {
          params: { action_type: "gs_diagnostic", score: gsScore },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // 2. Record behavior for Core Intelligence analytics
      const totalTimeUsed = 15 * 60 - timeLeft; // seconds used
      const totalAttempted = correct + wrong;
      await managedAxios.post(
        `${API_BASE}/intelligence/record-behavior`,
        {
          test_id: `gs_diagnostic_${Date.now()}`,
          test_type: "gs_diagnostic",
          total_questions: questions.length,
          attempted: totalAttempted,
          correct: correct,
          wrong: wrong,
          unattempted: skipped,
          score: gsScore,
          accuracy: totalAttempted > 0 ? (correct / totalAttempted) * 100 : 0,
          total_time_seconds: totalTimeUsed,
          avg_time_per_question: totalTimeUsed / (totalAttempted || 1),
          elimination_usage_rate: 50 // Default for GS
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // 3. Navigate to feedback page with results
      navigate("/diagnostic-feedback", {
        state: {
          testType: "gs",
          score: gsScore,
          status: status,
          correct: correct,
          total: questions.length,
          timeSpent: totalTimeUsed,
          weakestSubject: weakestSubject
        }
      });
      
    } catch (error) {
      console.error("Error submitting result:", error);
    }
    
    setSubmitting(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "safe": return "text-emerald-500";
      case "borderline": return "text-amber-500";
      case "unsafe": return "text-red-500";
      default: return "text-slate-500";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "safe": return <CheckCircle className="w-16 h-16 text-emerald-500" />;
      case "borderline": return <AlertTriangle className="w-16 h-16 text-amber-500" />;
      case "unsafe": return <XCircle className="w-16 h-16 text-red-500" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "safe": return "🟢 SAFE";
      case "borderline": return "🟡 BORDERLINE";
      case "unsafe": return "❌ UNSAFE";
      default: return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Pre-test screen
  if (!testStarted && !testComplete) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-0 bg-slate-800/50 backdrop-blur-xl">
          <CardContent className="p-8 text-center">
            <BookOpen className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">GS Foundation Check</h1>
            <p className="text-slate-400 mb-6">
              This diagnostic will assess your GS conceptual clarity.
            </p>
            
            <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-slate-300 mb-2">
                <Clock className="w-4 h-4" />
                <span>15 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300 mb-2">
                <CheckCircle className="w-4 h-4" />
                <span>15 questions</span>
              </div>
              <p className="text-sm text-slate-400 mt-3">
                Covers <strong>Polity, History, Geography, Science</strong>
              </p>
            </div>
            
            <Button
              onClick={() => setTestStarted(true)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-lg py-6"
            >
              Start Diagnostic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Result screen
  if (testComplete && result) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-0 bg-slate-800/50 backdrop-blur-xl">
          <CardContent className="p-8 text-center">
            {getStatusIcon(result.status)}
            
            <h1 className={`text-3xl font-bold mt-4 ${getStatusColor(result.status)}`}>
              {getStatusBadge(result.status)}
            </h1>
            
            <p className="text-slate-300 mt-4 text-lg">
              {result.reason}
            </p>
            
            <p className="text-slate-400 mt-2">
              {result.instruction}
            </p>
            
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-emerald-400">{result.correct}</p>
                <p className="text-xs text-slate-400">Correct</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-red-400">{result.wrong}</p>
                <p className="text-xs text-slate-400">Wrong</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-slate-400">{result.skipped}</p>
                <p className="text-xs text-slate-400">Skipped</p>
              </div>
            </div>
            
            {result.weakest_subject && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
                <p className="text-amber-400 text-sm">
                  ⚠️ Focus Area: <strong>{result.weakest_subject}</strong>
                </p>
              </div>
            )}
            
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 py-6"
            >
              Continue to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Test in progress
  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      <div className="max-w-3xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-400" />
            <span className="text-white font-medium">GS Foundation Check</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            timeLeft < 180 ? "bg-red-500/20 text-red-400" : "bg-slate-700 text-slate-300"
          }`}>
            <Timer className="w-4 h-4" />
            <span className="font-mono">{formatTime(timeLeft)}</span>
          </div>
        </div>
        
        <div className="flex gap-1 mt-4">
          {questions.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                answers[questions[idx]?.id]
                  ? "bg-emerald-500"
                  : idx === currentIndex
                  ? "bg-indigo-500"
                  : "bg-slate-700"
              }`}
            />
          ))}
        </div>
        <p className="text-slate-500 text-sm mt-2">
          {answeredCount} of {questions.length} answered
        </p>
      </div>

      <Card className="max-w-3xl mx-auto border-0 bg-slate-800/50 backdrop-blur-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500">Question {currentIndex + 1}</span>
            {currentQuestion.subject && (
              <span className="text-xs px-2 py-1 rounded-full bg-indigo-500/20 text-indigo-400">
                {currentQuestion.subject}
              </span>
            )}
          </div>
          
          <p className="text-white text-lg mb-6">
            {currentQuestion.question}
          </p>
          
          <div className="space-y-3">
            {Object.entries(currentQuestion.options).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleAnswer(key)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  answers[currentQuestion.id] === key
                    ? "border-indigo-500 bg-indigo-500/10 text-white"
                    : "border-slate-700 hover:border-slate-600 text-slate-300 hover:bg-slate-700/50"
                }`}
              >
                <span className="font-medium mr-3">{key}.</span>
                {value}
              </button>
            ))}
          </div>
          
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="border-slate-600"
            >
              Previous
            </Button>
            
            {currentIndex < questions.length - 1 ? (
              <Button onClick={handleNext} className="bg-indigo-600 hover:bg-indigo-700">
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? "Submitting..." : "Submit Test"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GSDiagnosticPage;
